import { toBig } from '@src/utils/number';
import { getPricingReference } from '@src/utils/pricingMode';
import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { get, isFunction } from 'lodash-es';
import { OrderStrategy, PricingModeEnum } from '../../../types';
import { AgGridPrice } from '../../AgGrid';
import { BlotterAggFunc } from '../aggFuncs';
import type { PriceValue } from '../aggFuncs/priceAggFunc';
import { baseColumn } from './baseColumn';
import type { PriceParams } from './price.types';
import type { ColDefFactory, Column } from './types';
import { numericColumnComparator } from './utils';

export const price: ColDefFactory<Column<PriceParams>> = column => ({
  initialWidth: 150,
  minWidth: 70,
  maxWidth: 210,
  ...baseColumn(column),
  type: 'numericColumn',
  cellEditor: 'input',

  cellRenderer: ({ value, data, colDef }: ICellRendererParams<unknown, PriceValue>) => {
    return <AgGridPrice value={value} data={data} colDef={colDef} {...column.params} />;
  },
  comparator: (a: PriceValue, b: PriceValue) => {
    return numericColumnComparator(a?.price, b?.price);
  },
  headerClass: () => {
    const columnGroupClass = column.columnGroup ? 'ag-custom-column-group' : '';
    return column.params?.align !== 'left' ? `ag-right-aligned-header ${columnGroupClass}` : `${columnGroupClass}`;
  },
  cellClass: () => {
    return column.params?.align !== 'left' ? 'ag-right-aligned-cell' : '';
  },
  // There is no official type provided for this callback I could find -- so typing the parameters of this callback manually for now.
  valueParser: ({ oldValue, newValue }: { oldValue: PriceValue; newValue: string | undefined }): PriceValue => {
    try {
      const newValueBig = toBig(newValue);
      if (!newValueBig) {
        return undefined;
      }

      return {
        asset: oldValue?.asset,
        quoteCurrency: oldValue?.quoteCurrency,
        aggregate: true,
        price: newValueBig,
        endPrice: oldValue?.endPrice,
        isBestPrice: false,
      };
    } catch (e) {
      return oldValue;
    }
  },
  valueGetter: ({ data, context }: ValueGetterParams<unknown>): PriceValue => {
    if (!data) {
      return undefined;
    }

    const priceField: string | undefined = column.field;
    const price: string | undefined = priceField ? get(data, priceField) : undefined;

    const isBestPrice = column.params?.getIsBestPrice?.(data) ?? false;

    // Sometimes a price is relative (a reference price) and not absolute!
    let isReferencePrice = column.params?.isReferencePrice ?? false;
    if (isFunction(column.params?.isReferencePrice)) {
      isReferencePrice = column.params.isReferencePrice(data);
    }
    if (isReferencePrice) {
      const referencePrice = getPricingReference(data, priceField);
      return {
        price: toBig(referencePrice?.value),
        quoteCurrency: referencePrice?.currency,
        endPrice: undefined,
        aggregate: false,
        asset: undefined,
        isBestPrice,
      };
    }

    // currency or security
    let quoteCurrency: string | undefined = undefined;
    let asset: string | undefined = undefined;

    const { asset: inputAsset, assetField, quoteCurrencyField, quoteCurrencySecurityField } = column.params ?? {};
    const resolvedAsset = inputAsset ?? (assetField ? get(data, assetField) : undefined);

    const maybeSecurity = resolvedAsset ? context.current.securitiesBySymbol?.get(resolvedAsset) : undefined;
    const maybeCurrency = resolvedAsset ? context.current.currenciesBySymbol?.get(resolvedAsset) : undefined;
    const maybeQuoteCurrency = quoteCurrencyField ? get(data, quoteCurrencyField) : undefined;
    const pricingMode = get(data, 'PricingMode');

    if (maybeSecurity) {
      asset = maybeSecurity.Symbol;
      quoteCurrency = get(maybeSecurity, quoteCurrencySecurityField ?? 'QuoteCurrency');
    } else if (maybeCurrency) {
      asset = maybeCurrency?.Symbol;
    }

    if (maybeQuoteCurrency) {
      quoteCurrency = maybeQuoteCurrency;
    }

    // Override quoteCurrency for BPS-priced orders
    // Their unit is always BPS regardless of quoteCurrency
    if (pricingMode === PricingModeEnum.BPS) {
      quoteCurrency = 'BPS';
    }

    // price band display eg 1000-2000
    let endPrice: string | undefined = undefined;
    const strategy = context.current.strategiesByName?.get(get(data, 'Strategy', ''));
    if (column.params?.showPriceAsRange && strategy && strategy instanceof OrderStrategy && strategy.showPriceAsRange) {
      endPrice = get(data, `Parameters.${strategy.endPriceParamName}`);
    }

    return {
      price: toBig(price),
      endPrice: toBig(endPrice),
      quoteCurrency,
      asset,
      aggregate: true,
      isBestPrice,
    };
  },
  // used for exports since we have implemented a cell renderer of our own
  valueFormatter: ({ value }: ValueFormatterParams<unknown, PriceValue>) => {
    if (!value || !value.price) {
      return '';
    }

    return value.price.toFixed();
  },
  initialAggFunc: column.initialAggFunc ?? (column.aggregate ? BlotterAggFunc.Price : undefined),
});
